// Chat send loop. Provider selection follows the session preference
// with one rule: if the user picked a provider they don't have a key
// for, we fall back rather than refuse.
//
//   provider_preference = "babyai"     → BabyAI (Mosh Pit if enabled), else Anthropic
//   provider_preference = "anthropic"  → Anthropic, else BabyAI
//   provider_preference = "auto"       → BabyAI Mosh Pit (the routing-LoRA signal path)
//
// The Playground's whole point is making BabyAI native, so "auto"
// and "babyai" are functionally the same in v0 — both go through
// the Mosh Pit when the session has it enabled.

import {
  callBabyAI,
  callBabyAIMoshPit,
  callBabyAIStream,
  type BabyAIMessage,
  type BabyAIMoshPitResult,
} from "./babyai-client";
import { getClient, DEFAULT_MODEL as DEFAULT_ANTHROPIC_MODEL } from "./anthropic-client";
import { useKeyStore } from "../store/keyStore";
import { useChannelKeyStore } from "../store/channelKeyStore";
import type { Session, ToolRound } from "./session";
import {
  MAX_AGENT_TURNS,
  REACT_TAIL,
  formatToolResultBlock,
  parseToolCalls,
  reactHead,
  stripToolBlocks,
} from "./react-protocol";
import { executeTool } from "./tools";

/** Resolve the keys to use for a given channel: channel-attached keys
 *  (decrypted, in sessionStorage) take precedence over personal BYOK. */
function resolveKeys(channelId: string): { babyai: string | null; anthropic: string | null } {
  const channelKeys = useChannelKeyStore.getState().get(channelId);
  const personal = useKeyStore.getState();
  return {
    babyai: channelKeys?.babyai ?? personal.babyai,
    anthropic: channelKeys?.anthropic ?? personal.anthropic,
  };
}

const SYSTEM_PROMPT =
  "You are the agent in a BabyAI Playground channel — a shared team " +
  "chat. The thread is visible to everyone in the channel. Be concise. " +
  "Post a checklist when working through a multi-step ask. Treat the " +
  "channel as the substrate: the conversation is the artifact.";

/** Build an attached-documents block to prepend to the system prompt.
 *  When docs ARE attached: lists them with the cite-when-you-ground
 *  instruction. When none are attached: emits a small nudge for the
 *  AI to suggest the user attach one IF the question would clearly
 *  benefit. Exported so brainstorm.ts can use the same shape — one
 *  place to edit if the framing changes. */
export function buildAttachedDocsBlock(session: Session): string {
  const docs = session.attached_documents ?? [];
  if (docs.length === 0) {
    // No-docs nudge: surfaces the library feature when the question
    // clearly references external context the AI doesn't have, but
    // stays silent for chit-chat and general-knowledge questions.
    return [
      "",
      "No documents are attached to this channel. IF the user's question",
      "clearly references a specific external document (a spec, a transcript,",
      "an RFC, a meeting they had) that you don't have, lead your answer with",
      "one short line: \"(consider attaching the relevant doc — I'm answering",
      "from general knowledge.)\". Skip this for casual conversation, general",
      "questions, or anything you can answer well without the source. Don't",
      "nudge more than once per channel.",
      "",
    ].join("\n");
  }
  const parts: string[] = [];
  parts.push("");
  parts.push("The following documents are attached to this channel as context.");
  parts.push("They are part of every turn's context — assume they're known to");
  parts.push("the team.");
  parts.push("");
  parts.push("CITE WHEN YOU GROUND: when a meaningful chunk of your answer or");
  parts.push("whiteboard rewrite draws on a specific attached document, lead");
  parts.push("the relevant section with a one-line citation in the form");
  parts.push("`From \\`doc-name.md\\`:` (backticks around the name, the exact");
  parts.push("filename as listed below). Skip citations for incidental references");
  parts.push("or general knowledge — only cite when the doc materially shaped");
  parts.push("the answer. The team needs to see where your ground is.");
  for (const d of docs) {
    parts.push("");
    parts.push(`<attached_document name="${d.name}">`);
    parts.push(d.content);
    parts.push("</attached_document>");
  }
  parts.push("");
  return parts.join("\n");
}

export type ChatSendResult =
  | { kind: "single"; text: string; model: string }
  | { kind: "mosh_pit"; result: BabyAIMoshPitResult }
  | { kind: "agent"; text: string; model: string; tool_rounds: ToolRound[] };

export interface ChatSendOptions {
  session: Session;
  onToken?: (delta: string) => void;
}

export async function sendChat(opts: ChatSendOptions): Promise<ChatSendResult> {
  const keys = resolveKeys(opts.session.id);
  const messages = sessionToBabyAI(opts.session);
  const usePublicBabyAI = useKeyStore.getState().usePublic;
  const babyaiAvailable = !!keys.babyai || usePublicBabyAI;

  const wantBabyAI = opts.session.provider_preference !== "anthropic";
  const useBabyAI = wantBabyAI ? babyaiAvailable : false;
  const useAnthropic = useBabyAI ? false : !!keys.anthropic;

  if (!useBabyAI && !useAnthropic) {
    if (babyaiAvailable) return routeBabyAI(opts.session, messages, keys.babyai, opts.onToken);
    if (keys.anthropic) return routeAnthropic(opts.session, messages, keys.anthropic, opts.onToken);
    throw new Error(
      "No BabyAI access. Add a key in the Keys panel, turn on \"Use public BabyAI\" there, " +
      "or unlock the channel's attached keys.",
    );
  }

  if (useBabyAI) return routeBabyAI(opts.session, messages, keys.babyai, opts.onToken);
  return routeAnthropic(opts.session, messages, keys.anthropic!, opts.onToken);
}

function routeBabyAI(
  session: Session,
  messages: BabyAIMessage[],
  token: string | null,
  onToken?: (delta: string) => void,
): Promise<ChatSendResult> {
  // `token` may be null when the user is in public mode — babyai-client
  // resolves to the proxy when no explicit token is provided.
  const passToken = token ?? undefined;
  if (session.tools_enabled) return runAgentLoopBabyAI(session, messages, passToken);
  return sendBabyAI(session, messages, passToken, onToken);
}

function routeAnthropic(
  session: Session,
  messages: BabyAIMessage[],
  apiKey: string,
  onToken?: (delta: string) => void,
): Promise<ChatSendResult> {
  if (session.tools_enabled) return runAgentLoopAnthropic(session, messages, apiKey);
  return sendAnthropic(apiKey, session, messages, onToken);
}

// ---------------------------------------------------------------------------
// Agent loop (BabyAI + Anthropic share the same ReAct protocol; the only
// difference is which call function we use)
// ---------------------------------------------------------------------------

async function runAgentLoopBabyAI(
  session: Session,
  history: BabyAIMessage[],
  token: string | undefined,
): Promise<ChatSendResult> {
  return runAgentLoop(session, history, async (msgs, systemInstruction) => {
    const result = await callBabyAI({
      modelId: "auto",
      messages: msgs,
      systemInstruction,
      maxTokens: 1500,
      token,
    });
    return { text: result.text, model: result.model };
  });
}

async function runAgentLoopAnthropic(
  session: Session,
  history: BabyAIMessage[],
  apiKey: string,
): Promise<ChatSendResult> {
  const client = await getClient(apiKey);
  return runAgentLoop(session, history, async (msgs, systemInstruction) => {
    const anthropicMessages = msgs
      .filter((m) => m.role !== "system")
      .map((m) => ({ role: m.role as "user" | "assistant", content: m.content }));
    const resp = await client.messages.create({
      model: DEFAULT_ANTHROPIC_MODEL,
      max_tokens: 1500,
      system: systemInstruction,
      messages: anthropicMessages,
    });
    const text = resp.content
      .filter((c) => c.type === "text")
      .map((c) => (c as { text: string }).text)
      .join("");
    return { text, model: DEFAULT_ANTHROPIC_MODEL };
  });
}

type ModelCall = (
  msgs: BabyAIMessage[],
  systemInstruction: string,
) => Promise<{ text: string; model: string }>;

async function runAgentLoop(
  session: Session,
  history: BabyAIMessage[],
  callModel: ModelCall,
): Promise<ChatSendResult> {
  const systemInstruction = reactHead() + REACT_TAIL + buildAttachedDocsBlock(session);
  const workingMessages: BabyAIMessage[] = [...history];
  const tool_rounds: ToolRound[] = [];
  let lastModel = "";

  for (let turn = 0; turn < MAX_AGENT_TURNS; turn++) {
    const { text, model } = await callModel(workingMessages, systemInstruction);
    lastModel = model;

    const calls = parseToolCalls(text);
    const chatText = stripToolBlocks(text);

    // Push the assistant turn (full text including tool blocks) into
    // history so the model sees its own prior emissions on the next
    // turn.
    workingMessages.push({ role: "assistant", content: text });

    if (calls.length === 0) {
      // No tool blocks → loop terminates. The text is the final answer.
      return {
        kind: "agent",
        text: chatText || "(no response)",
        model: lastModel,
        tool_rounds,
      };
    }

    // Execute every call this turn; bundle results as one user message.
    const resultBlocks: string[] = [];
    for (const c of calls) {
      let args: unknown = {};
      let argsParseError: string | null = null;
      try {
        args = c.rawArgs ? JSON.parse(c.rawArgs) : {};
      } catch (e) {
        argsParseError = e instanceof Error ? e.message : String(e);
      }

      if (argsParseError) {
        const detail = `error: tool args were not valid JSON (${argsParseError}). Got:\n${c.rawArgs.slice(0, 400)}\n\nReply with a corrected <playground_tool> block using valid JSON.`;
        tool_rounds.push({ name: c.name, args: c.rawArgs, ok: false, detail });
        resultBlocks.push(formatToolResultBlock(c.name, false, detail));
        continue;
      }

      const out = await executeTool(c.name, args);
      tool_rounds.push({ name: c.name, args, ok: out.ok, detail: out.detail });
      resultBlocks.push(formatToolResultBlock(c.name, out.ok, out.detail));
    }

    workingMessages.push({ role: "user", content: resultBlocks.join("\n\n") });
  }

  // Hit the turn cap without convergence. Return whatever rounds we
  // accumulated with a flagged final message.
  return {
    kind: "agent",
    text: "(hit the agent turn cap without a final answer — try a simpler ask, or break it into steps)",
    model: lastModel,
    tool_rounds,
  };
}

async function sendBabyAI(
  session: Session,
  messages: BabyAIMessage[],
  token: string | undefined,
  onToken?: (delta: string) => void,
): Promise<ChatSendResult> {
  // Mosh Pit is non-streaming. If streaming was requested AND Mosh Pit
  // is on, we still do Mosh Pit — the tab-pick is more valuable than
  // the streaming UX for a teams app. Single-model streaming kicks in
  // when Mosh Pit is off.
  const sysPrompt = SYSTEM_PROMPT + buildAttachedDocsBlock(session);
  if (session.mosh_pit_enabled) {
    const result = await callBabyAIMoshPit({
      messages,
      systemInstruction: sysPrompt,
      maxTokens: 1024,
      token,
    });
    return { kind: "mosh_pit", result };
  }

  const result = await callBabyAIStream({
    modelId: "auto",
    messages,
    systemInstruction: sysPrompt,
    maxTokens: 1024,
    token,
    onToken: onToken ?? (() => {}),
  });
  return { kind: "single", text: result.text, model: result.model };
}

async function sendAnthropic(
  apiKey: string,
  session: Session,
  messages: BabyAIMessage[],
  onToken?: (delta: string) => void,
): Promise<ChatSendResult> {
  const client = await getClient(apiKey);

  // Anthropic's `messages` API doesn't take a system role inside the
  // array — it goes top-level.
  const anthropicMessages = messages
    .filter((m) => m.role !== "system")
    .map((m) => ({ role: m.role as "user" | "assistant", content: m.content }));

  const sysPrompt = SYSTEM_PROMPT + buildAttachedDocsBlock(session);
  let text = "";
  if (onToken) {
    const stream = client.messages.stream({
      model: DEFAULT_ANTHROPIC_MODEL,
      max_tokens: 1024,
      system: sysPrompt,
      messages: anthropicMessages,
    });
    stream.on("text", (delta) => {
      text += delta;
      onToken(delta);
    });
    await stream.finalMessage();
  } else {
    const resp = await client.messages.create({
      model: DEFAULT_ANTHROPIC_MODEL,
      max_tokens: 1024,
      system: sysPrompt,
      messages: anthropicMessages,
    });
    text = resp.content
      .filter((c) => c.type === "text")
      .map((c) => (c as { text: string }).text)
      .join("");
  }

  return { kind: "single", text, model: DEFAULT_ANTHROPIC_MODEL };
}

function sessionToBabyAI(session: Session): BabyAIMessage[] {
  return session.messages
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map((m) => ({ role: m.role as "user" | "assistant", content: m.content }));
}
